import React, { useMemo } from 'react'
import { Bar, Column, Pie } from '@ant-design/plots'

type ChartPoint = { label: string, value: number }
type Props = { options: any, series: any, type: 'bar' | 'donut', height: number }

/**
 * Stable chart adapter for Broker Analytics.
 * It preserves the page's compact chart contract while avoiding Apex runtime
 * state mutations when filters or chart types change.
 */
export default function BrokerAnalyticsChart({ options, series, type, height }: Props): JSX.Element {
    const isDonut = type === 'donut'
    const isHorizontal = Boolean(options?.plotOptions?.bar?.horizontal)
    const data = useMemo<ChartPoint[]>(() => {
        const labels: string[] = Array.isArray(options?.labels) && options.labels.length > 0
            ? options.labels
            : Array.isArray(options?.xaxis?.categories) ? options.xaxis.categories : []
        const values: number[] = isDonut
            ? (Array.isArray(series) ? series : [])
            : (Array.isArray(series?.[0]?.data) ? series[0].data : [])
        return values.map((value, index) => ({ label: labels[index] ?? `Item ${index + 1}`, value: Number(value) || 0 }))
    }, [isDonut, options?.labels, options?.xaxis?.categories, series])

    if (isDonut) {
        const total = data.reduce((sum, item) => sum + item.value, 0)
        const pieConfig: any = { data, angleField: 'value', colorField: 'label', radius: 0.9, innerRadius: 0.64, height, appendPadding: 8, legend: false, label: false, statistic: { title: false, content: { content: String(total) } }, tooltip: { formatter: (datum: any) => ({ name: datum.label, value: `${datum.value} brokers · ${total > 0 ? Math.round(datum.value / total * 100) : 0}%` }) } }
        return <Pie {...pieConfig} />
    }

    const suffix = series?.[0]?.name === 'Paid Bricks' ? ' paid Bricks' : ' brokers'
    const config: any = { data, height, xField: isHorizontal ? 'value' : 'label', yField: isHorizontal ? 'label' : 'value', color: '#009EF7', legend: false, animation: { appear: { animation: 'fade-in' } }, label: { position: isHorizontal ? 'right' : 'top', style: { fill: '#5e6278', fontSize: 11 } }, tooltip: { formatter: (datum: any) => ({ name: datum.label, value: `${datum.value}${suffix}` }) } }
    return isHorizontal ? <Bar {...config} /> : <Column {...config} />
}
